// Database configuration
const DB_NAME = 'expense-processor-db';
const DB_VERSION = 1;

// Store names
export const STORES = {
  SETTINGS: 'settings',
  EXPENSES: 'expenses',
  RULES: 'rules',
  BUDGETS: 'budgets'
};

// Settings keys
export const SETTINGS_KEYS = {
  COLUMN_WIDTHS: 'column-widths'
};

// Initialize the database
export const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = (event) => {
      console.error('Error opening IndexedDB', event);
      reject('Error opening IndexedDB');
    };
    
    request.onsuccess = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      resolve(db);
    };
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Create stores if they don't exist
      if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
        db.createObjectStore(STORES.SETTINGS, { keyPath: 'id' });
      }
      
      if (!db.objectStoreNames.contains(STORES.EXPENSES)) {
        db.createObjectStore(STORES.EXPENSES, { keyPath: 'id', autoIncrement: true });
      }
      
      if (!db.objectStoreNames.contains(STORES.RULES)) {
        db.createObjectStore(STORES.RULES, { keyPath: 'id', autoIncrement: true });
      }
      
      if (!db.objectStoreNames.contains(STORES.BUDGETS)) {
        db.createObjectStore(STORES.BUDGETS, { keyPath: 'id', autoIncrement: true });
      }
    };
  });
};

// Save settings item
export const saveSettings = async <T>(key: string, data: T): Promise<void> => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SETTINGS], 'readwrite');
      const store = transaction.objectStore(STORES.SETTINGS);
      
      const request = store.put({ id: key, value: data });
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = (event) => {
        console.error('Error saving settings', event);
        reject('Error saving settings');
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('Failed to save settings', error);
    // Fall back to localStorage for critical settings
    try {
      localStorage.setItem(`expense-processor-${key}`, JSON.stringify(data));
    } catch (e) {
      console.error('Failed to save to localStorage as well', e);
    }
  }
};

// Get settings item
export const getSettings = async <T>(key: string, defaultValue: T): Promise<T> => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SETTINGS], 'readonly');
      const store = transaction.objectStore(STORES.SETTINGS);
      
      const request = store.get(key);
      
      request.onsuccess = (event) => {
        const result = (event.target as IDBRequest).result;
        if (result) {
          resolve(result.value);
        } else {
          resolve(defaultValue);
        }
      };
      
      request.onerror = (event) => {
        console.error('Error getting settings', event);
        reject('Error getting settings');
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('Failed to get settings', error);
    // Try to get from localStorage as fallback
    try {
      const data = localStorage.getItem(`expense-processor-${key}`);
      if (data) {
        return JSON.parse(data) as T;
      }
    } catch (e) {
      console.error('Failed to get from localStorage as well', e);
    }
    return defaultValue;
  }
};

// Generic add item to store function
export const addItem = async <T>(storeName: string, item: T): Promise<number> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const request = store.add(item);
    
    request.onsuccess = (event) => {
      const id = (event.target as IDBRequest).result as number;
      resolve(id);
    };
    
    request.onerror = (event) => {
      console.error(`Error adding item to ${storeName}`, event);
      reject(`Error adding item to ${storeName}`);
    };
    
    transaction.oncomplete = () => {
      db.close();
    };
  });
};

// Generic get all items from store function
export const getAllItems = async <T>(storeName: string): Promise<T[]> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    
    const request = store.getAll();
    
    request.onsuccess = (event) => {
      const items = (event.target as IDBRequest).result as T[];
      resolve(items);
    };
    
    request.onerror = (event) => {
      console.error(`Error getting items from ${storeName}`, event);
      reject(`Error getting items from ${storeName}`);
    };
    
    transaction.oncomplete = () => {
      db.close();
    };
  });
};

// Generic update item in store function
export const updateItem = async <T>(storeName: string, id: number | string, item: T): Promise<void> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    // For settings store, the id is already part of the item
    const request = storeName === STORES.SETTINGS 
      ? store.put(item) 
      : store.put({ ...item, id });
    
    request.onsuccess = () => {
      resolve();
    };
    
    request.onerror = (event) => {
      console.error(`Error updating item in ${storeName}`, event);
      reject(`Error updating item in ${storeName}`);
    };
    
    transaction.oncomplete = () => {
      db.close();
    };
  });
};

// Generic delete item from store function
export const deleteItem = async (storeName: string, id: number | string): Promise<void> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const request = store.delete(id);
    
    request.onsuccess = () => {
      resolve();
    };
    
    request.onerror = (event) => {
      console.error(`Error deleting item from ${storeName}`, event);
      reject(`Error deleting item from ${storeName}`);
    };
    
    transaction.oncomplete = () => {
      db.close();
    };
  });
};

// Clear all data from a store
export const clearStore = async (storeName: string): Promise<void> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const request = store.clear();
    
    request.onsuccess = () => {
      resolve();
    };
    
    request.onerror = (event) => {
      console.error(`Error clearing store ${storeName}`, event);
      reject(`Error clearing store ${storeName}`);
    };
    
    transaction.oncomplete = () => {
      db.close();
    };
  });
}; 